import { useModalMeta } from "@/shared/atoms/useModal";
import useCategoryHook from "@/shared/hooks/useCategoryHook";
import useCloudinaryUploadHook from "@/shared/hooks/useCloudinaryUploadHook";
import { MenuItems } from "@/shared/utils/enum";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cleanCharFromObject, guidGenerator } from "../../shared/utils/helpers";
import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";
import Modal, { ModalProps } from "../Modal/Modal";
import ModalTitle from "../Modal/ModalTitle";
import ToppingModalStandard from "../Menu/ToppingModalStandard";
import ToppingModalAdvance from "../Menu/ToppingModalAdvance";
import { useState } from "react";
import IconToolTip from "../Icon/IconToolTip";

const MenuToppingModal = ({ onExit, ...props }: ModalProps) => {
  const { t } = useTranslation();
  const [{ defaultValue, refetchToppings }] = useModalMeta();
  const { toppingCreate, toppingRemove, toppingUpdate } = useCategoryHook();
  const { uploadImage } = useCloudinaryUploadHook();
  const methods = useForm();
  const { handleSubmit, control } = methods;
  const [isStandard, setIsStandard] = useState(true);

  const onFormSubmit = async (data) => {
    let req = {
      ...defaultValue,
      ...data,
      code: defaultValue?.code || guidGenerator(),
      price_per_target_value: cleanCharFromObject(data.price_per_target_value),
      vat_enum_per_target: cleanCharFromObject(data.vat_enum_per_target),
    };
    if (
      req.image_cloudinary_id &&
      req?.image_cloudinary_id?.size !== undefined
    ) {
      const image_cloudinary_id = await uploadImage(
        "item-image",
        req.image_cloudinary_id,
        undefined
      );
      req = { ...req, image_cloudinary_id };
    }

    if (defaultValue?.code === undefined) {
      toppingCreate.mutateAsync(
        { ...req, item_type: MenuItems.TOPPING },
        {
          onSuccess: () => {
            onExit();
            refetchToppings();
          },
        }
      );
    } else {
      toppingUpdate.mutateAsync(req, {
        onSuccess: () => {
          onExit();
          refetchToppings();
        },
      });
    }
  };

  return (
    <Modal
      {...props}
      width={{ _: "90%", md: "100rem" }}
      onExit={() => onExit()}
    >
      <ModalTitle>
        {defaultValue?.code
          ? t("toppings.extrasUpdate")
          : t("toppings.extrasAdd")}
      </ModalTitle>

      <div
        className="mb-4 mr-[-0.8rem] flex justify-end"
        onClick={() => setIsStandard(!isStandard)}
      >
        <IconToolTip
          text={
            isStandard ? t("menu.advanceSettings") : t("menu.standardSettings")
          }
          variants={isStandard ? "gear" : "reply"}
          className="fill-primary"
        />
      </div>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <ToppingModalStandard
            defaultValue={defaultValue}
            isShown={isStandard}
          />
          <ToppingModalAdvance
            defaultValue={defaultValue}
            isShown={!isStandard}
          />

          <div className="mt-12 flex w-full items-center justify-end">
            <div className="flex-[1_1_100%] md:flex-[unset] m-[1rem]">
              <Controller
                name="is_active"
                control={control}
                defaultValue={defaultValue?.is_active ?? true}
                render={(props) => {
                  return (
                    <Checkbox
                      active={props.value}
                      checked={props.value}
                      onChange={(e) => props.onChange(e.target.checked)}
                    >
                      {t("toppings.active")}
                    </Checkbox>
                  );
                }}
              />
            </div>
            {defaultValue?.item_id && (
              <div className="flex-[1_0_auto] md:flex-[unset] m-[1rem]">
                <Button
                  type="button"
                  isPending={toppingRemove.isPending}
                  onClick={() =>
                    toppingRemove.mutateAsync(defaultValue?.item_id, {
                      onSuccess: () => onExit(),
                    })
                  }
                  variant="danger"
                >
                  {t("toppings.delete")}
                </Button>
              </div>
            )}
            <div className="m-[1rem] flex-[1_0_auto] md:flex-[unset]">
              <Button
                isPending={toppingUpdate.isPending || toppingCreate.isPending}
              >
                {t("toppings.save")}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default MenuToppingModal;
